import type { ComponentDiagnostic } from "@swot/base-components";
import { CmtsHttpClient, apiPost, type CmtsHttpClientOptions } from "@cmts/api/httpClient.js";
import { apiRoutes, buildApiRoute } from "@cmts/api/apiPaths.js";
import { clearCmtsSession, getStoredAccessToken, getStoredRefreshToken, mergeCmtsSession } from "@cmts/auth/authStore.js";
import { createCmtsRepositorySuite, type CmtsRepositorySuite } from "@cmts/repositories/cmtsRepositorySuite.js";
import { createCmtsHostAppConfig, validateCmtsHostAppConfig, type CmtsHostAppConfig } from "./appConfig.js";
import { cmtsStore } from "@store/index.js";
import { uiActions } from "@store/uiSlice.js";
import { authActions } from "@store/authSlice.js";
import { CmtsApiError } from "@cmts/api/cmtsApiErrors.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";

export type CmtsHostRuntime = {
  config: CmtsHostAppConfig;
  client: CmtsHttpClient;
  repositories: CmtsRepositorySuite;
  diagnostics: readonly ComponentDiagnostic[];
};

/**
 * Creates the backend-connected production runtime.
 * Demo/offline transport is intentionally isolated in appRuntime.demo.ts.
 */
export function createCmtsHostRuntime(
  overrides: Partial<CmtsHostAppConfig> = {},
  httpOverrides: Partial<CmtsHttpClientOptions> = {},
): CmtsHostRuntime {
  const config = createCmtsHostAppConfig({
    ...overrides,
    environmentMode: "live",
    runtimeMode: "LIVE_BACKEND",
    resolvedRuntimeMode: "LIVE_BACKEND",
    enableOfflineDemo: false,
    enableDemoPersonaLogin: false,
    enableDemoSeedReset: false,
    enableLiveBackend: true,
    enableDemoFallbacks: false,
    tenantId: undefined,
  });
  const diagnostics = validateCmtsHostAppConfig(config);
  const client = new CmtsHttpClient({
    baseUrl: config.apiBaseUrl,
    timeoutMs: config.requestTimeoutMs,
    getToken: () => getStoredAccessToken(),
    refreshToken: async () => refreshFromStoredToken(),
    getTenantCode: () => config.tenantCode,
    onUnauthorized: () => {
      clearCmtsSession();
      cmtsStore.dispatch(authActions.tokenRefreshFailed({
        message: "Authentication expired or was rejected by the CMTS API.",
        atUtc: new Date().toISOString(),
      }));
    },
    onDiagnostics: (event) => {
      cmtsStore.dispatch(uiActions.correlationRecorded(event.correlationId));
      if (event.error instanceof CmtsApiError) {
        cmtsStore.dispatch(uiActions.apiErrorRecorded(event.error.envelope));
      }
      httpOverrides.onDiagnostics?.(event);
    },
    ...httpOverrides,
  });
  return { config, client, repositories: createCmtsRepositorySuite(client), diagnostics };
}

async function refreshFromStoredToken(): Promise<string | undefined> {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return undefined;
  try {
    const response = await apiPost<{
      accessToken?: string | undefined;
      refreshToken?: string | undefined;
      expiresAtUtc?: string | undefined;
    }>(
      buildApiRoute(apiRoutes.auth.refresh),
      { refreshToken },
      { skipRefresh: true },
    );
    if (response.accessToken) mergeCmtsSession(response);
    return response.accessToken;
  } catch (error: unknown) {
    clearCmtsSession();
    cmtsStore.dispatch(authActions.tokenRefreshFailed({
      message: normalizeUserSafeApiError(error).userMessage || "Token refresh failed.",
      atUtc: new Date().toISOString(),
    }));
    return undefined;
  }
}
